(ns practitest.frontend.integration-settings.shared-form
  (:require [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.common.tooltip :as common-tooltip]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components.selector :as selector2]))

(defn transform-project-for-selector [project]
  {:id         (keyword (:id project))
   :label      (:name project)
   :has-prefix (boolean (:has_prefix project))})

(defn filter-selected-projects [projects selected-project-ids]
  (set (filter #(contains? selected-project-ids (:id %)) projects)))

(defn get-sync-tooltip-text [entity-type bugtracker]
  (if (= entity-type :requirements)
    (format-translation (get-translation "bugtracker.integration-settings.requirements-sync.tooltip"
                                         "Enable synchronization between %{bugtracker} and PractiTest Requirements")
                        :bugtracker bugtracker)
    (format-translation (get-translation "bugtracker.integration-settings.issues-sync.tooltip"
                                         "Enable synchronization between %{bugtracker} and PractiTest Issues")
                        :bugtracker bugtracker)))

(defn get-entity-label [entity-type]
  (if (= entity-type :requirements) "requirement" "issue"))

(defn get-other-entity-type [entity-type]
  (if (= entity-type :requirements) :issues :requirements))

(defn get-pt-entity-label [entity-type]
  (if (= entity-type :requirements)
    (get-translation "requirement.singular" "Requirement")
    (get-translation "issue.singular" "Issue")))

;; Removes types already claimed by the other entity (issue vs requirement) on the same
;; project, so the same type can't be double-mapped.
(defn get-available-type-options
  [all-types current-mappings project-id entity-type other-entity-enabled?]
  (let [other-entity-type    (get-other-entity-type entity-type)
        other-entity-map     (get-in current-mappings [(keyword project-id) other-entity-type])
        other-is-disabled?   (:disabled other-entity-map false)
        other-selected-types (if (or other-is-disabled? (not other-entity-enabled?))
                               #{}
                               (set (:ex_type other-entity-map [])))]
    (vec (remove #(contains? other-selected-types (:id %)) all-types))))

;; Flat project selector for bugtrackers without a two-tier space/list model (YouTrack, ServiceNow).
(defn project-selector
  [{:keys [projects-for-selector selected-projects bugtracker on-change-fn]}]
  [:div.form-group.integration-project-selector.no-bottom-divider
   [:label (format-translation (get-translation "bugtracker.integration-settings.select-project"
                                                "Select %{bugtracker} Projects to Sync")
                               :bugtracker bugtracker) ":"]
   [selector2/new-select
    {:id          "project-selector"
     :multi?      true
     :tags?       false
     :value       (filter-selected-projects projects-for-selector selected-projects)
     :items       projects-for-selector
     :key-fn      :id
     :label-fn    :label
     :placeholder (str (get-translation "bugtracker.integration-settings.select-project.placeholder" "Select projects") "...")
     :on-change   (fn [selected-set]
                    (let [selected-ids (set (map :id selected-set))]
                      (mixpanel/track (str bugtracker " Integration Settings") {"Action" (str "Selected " bugtracker " Projects")})
                      (on-change-fn selected-ids)))}]])

(defn default-project-selector
  [{:keys [projects-for-selector selected-projects default-project bugtracker on-change-fn]}]
  (let [selected-set (filter-selected-projects projects-for-selector selected-projects)
        disabled?    (empty? selected-set)
        value        (some #(when (= (:id %) (keyword default-project)) %) selected-set)]
    [:div.form-group.integration-default-project-selector
     [:label
      (format-translation (get-translation "bugtracker.integration-settings.youtrack.select-default-project"
                                           "Select %{bugtracker} Default Project")
                          :bugtracker bugtracker)
      " "
      [common-tooltip/info-circle-tooltip
       [:span (format-translation (get-translation "bugtracker.integration-settings.youtrack.default-project.help"
                                                    (str "The default project will be used each time your users create "
                                                         "an issue in %{bugtracker} after a test failure (users can "
                                                         "change it when creating the issue)."))
                                  :bugtracker bugtracker)]
       {:class "icon-info"}]]
     [selector2/new-select
      {:id          "default-project-selector"
       :multi?      false
       :tags?       false
       :disabled?   disabled?
       :value       value
       :items       (vec selected-set)
       :key-fn      :id
       :label-fn    :label
       :placeholder (str (get-translation "bugtracker.integration-settings.youtrack.select-default-project.placeholder"
                                          "Select a project above first"))
       :on-change   (fn [selected]
                      (when selected
                        (mixpanel/track (str bugtracker " Integration Settings") {"Action" (str "Selected " bugtracker " Default Project")})
                        (on-change-fn (name (:id selected)))))}]]))

;; Selected projects no longer visible to the current token (access revoked, deleted upstream)
;; get :accessible? false instead of being dropped, so project-mapping-row can render them "locked".
(defn build-projects-for-section
  [selected-project-ids available-projects configured-projects]
  (let [available-by-id  (into {} (map (fn [p] [(keyword (:id p)) p]) available-projects))
        configured-by-id (into {} (map (fn [p] [(keyword (:id p)) p]) configured-projects))]
    (->> selected-project-ids
         (keep (fn [pid]
                 (or (when-let [p (get available-by-id pid)]
                       {:id pid :label (:name p) :accessible? true})
                     (when-let [p (get configured-by-id pid)]
                       {:id pid :label (:name p) :accessible? false}))))
         vec)))

(defn checkbox [{:keys [checked? on-change disabled? disabled-tooltip]}]
  [:input.m-checkbox
   {:type      "checkbox"
    :checked   checked?
    :disabled  disabled?
    :title     (when disabled? disabled-tooltip)
    :on-change on-change}])

(defn toggle-switch [{:keys [enabled? on-change disabled?]}]
  [:div.form-switch
   [:input.form-check-input
    {:type      "checkbox"
     :checked   enabled?
     :disabled  disabled?
     :on-change #(on-change (.. % -target -checked))}]])

;; Generic row used in both JIRA and Azure entity mapping sections.
;; Props:
;;   :project-name   - display name of the project (read-only)
;;   :type-options   - seq of {:id :label} for the type dropdown
;;   :selected-types - seq of selected type IDs
;;   :pt-entity      - display label for the PT entity (read-only)
;;   :placeholder    - placeholder text for the type selector
;;   :on-types-change - fn called with new vec of selected type IDs
;;   :on-toggle      - fn called when enable/disable link is clicked
;;   :disabled?      - whether this row is disabled
;;   :types-loading? - whether types are still being fetched
;;   :on-search-change - optional fn called with the type-selector's search text as the user types
;;   :search-loading?  - optional flag showing a loading indicator while a server-side search is in flight
(defn project-mapping-row
  [{:keys [project-name type-options selected-types pt-entity placeholder
           on-types-change on-toggle disabled? types-loading? locked? locked-tooltip
           on-search-change search-loading?]}]
  (let [enable-disable-content
        (if disabled?
          [:<> [icons/mux-circle-plus] " " (get-translation "generic.label.enable" "Enable")]
          [:<> [icons/mux-circle-less] " " (get-translation "generic.label.disable" "Disable")])]
    [:div.mapping-row
     {:class (dom/classes-pairs :disabled disabled? :locked locked?)}
     [:span.locked-icon-slot
      (when locked?
        [tooltip/mux-tooltip
         {:position "top"}
         [:span locked-tooltip]
         (fn [tooltip-props]
           [:span.locked-icon tooltip-props [icons/mux-no-access]])])]
     [:input.form-control.project-name-input
      {:type     "text"
       :value    project-name
       :disabled true}]
     [:div.type-selector-wrapper
      [selector2/new-select
       {:multi?      true
        :disabled?   (or disabled? locked?)
        :tags?       false
        :value       (set (filter #(contains? (set selected-types) (:id %)) type-options))
        :items       type-options
        :key-fn      :id
        :label-fn    :label
        :placeholder (str placeholder "...")
        :on-change   (fn [selected-set]
                       (on-types-change (vec (map :id selected-set))))
        :on-search-change on-search-change}]
      (when (or types-loading? search-loading?)
        [:span.type-selector-loading
         [spinners/mux-spinner {:size :text}]])]
     [icons/mux-up-down]
     [:input.form-control.pt-entity-input
      {:type     "text"
       :value    pt-entity
       :disabled true}]
     (if locked?
       [:span.disable-link.locked-link enable-disable-content]
       [:a.disable-link
        {:href     "#"
         :on-click (fn [e]
                     (.preventDefault e)
                     (on-toggle))}
        enable-disable-content])]))

(defn synchronization-section
  [{:keys [title enabled? other-entity-enabled? on-toggle projects current-mappings
           entity-type bugtracker mapping-item-component lists-loading?]}]
  [:div.sync-section
   [:div.sync-header
    [:div.sync-title
     [:h5 title]
     [common-tooltip/info-circle-tooltip
      [:span (get-sync-tooltip-text entity-type bugtracker)]
      {:class "icon-info"}]]
    [toggle-switch {:enabled?  enabled?
                    :on-change on-toggle}]]
   (when enabled?
     [:<>
      [:div.project-mappings
       (for [project projects]
         ^{:key (name (:id project))}
         [mapping-item-component
          {:project               project
           :current-mappings      current-mappings
           :entity-type           entity-type
           :other-entity-enabled? other-entity-enabled?}])]
      (when lists-loading?
        [spinners/mux-spinner {:size :text}])])])

(defn general-settings-section
  [{:keys [allow-issue-creation? use-pt-id? skip-mapping? bugtracker
           on-allow-issue-creation on-use-pt-id on-skip-mapping]}]
  [:div.general-settings
   [:h5 (get-translation "bugtracker.integration-settings.general-settings" "General Settings")]
   [:div.checkbox
    [:label
     [checkbox {:checked?  allow-issue-creation?
                :on-change (fn [e]
                             (on-allow-issue-creation (.. e -target -checked)))}]
     (get-translation "bugtracker.integration-settings.checkbox.issue-creation"
                      "Allow to create Issues in PractiTest that are not linked to Integration")]]
   [:div.checkbox
    [:label
     [checkbox {:disabled? (not allow-issue-creation?)
                :checked?  use-pt-id?
                :on-change (fn [e]
                             (on-use-pt-id (.. e -target -checked)))}]
     (get-translation "bugtracker.integration-settings.checkbox.use-pt-ids"
                      "Use PractiTest ID for synced Issues")]]
   [:div.checkbox
    [:label
     [checkbox {:checked?  skip-mapping?
                :on-change (fn [e]
                             (on-skip-mapping (.. e -target -checked)))}]
     (get-translation "bugtracker.integration-settings.checkbox.skip-mapping"
                      "Do not enforce mapping")]
    " "
    [common-tooltip/info-circle-tooltip
     [:span (format-translation (get-translation "bugtracker.integration-settings.skip-mapping.tooltip"
                                                  "Selecting this option will disable restrictions for mapped entities. Any %{bugtracker} entity can be synced as a requirement or an issue.")
                                :bugtracker bugtracker)]
     {:class "icon-info"}]]])

(defn form-actions
  [{:keys [saving? dirty? on-cancel on-save]}]
  [:div.form-actions
   [buttons/button
    {:text      (get-translation "generic.button.cancel" "Cancel")
     :disabled? (or saving? (not dirty?))
     :on-click  on-cancel}]
   [buttons/button
    {:text      (if saving?
                  (str (get-translation "generic.label.saving" "Saving") "...")
                  (get-translation "jira-integration-settings.button.update-settings" "Update Settings"))
     :primary?  true
     :disabled? (or saving? (not dirty?))
     :on-click  on-save}]])
